import { useState } from 'react';
import { SEO, generateProfessionalServiceSchema, generateFAQSchema } from '../../utils/seo';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    tipo_atendimento: '',
    mensagem: ''
  });
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'mensagem') {
      setCharCount(value.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4fphsv2vmuha04krsrg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          tipo_atendimento: '',
          mensagem: ''
        });
        setCharCount(0);
      } else {
        setSubmitMessage('Erro ao enviar mensagem. Por favor, tente novamente.');
      }
    } catch (error) {
      setSubmitMessage('Erro ao enviar mensagem. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqData = [
    {
      question: 'Como funcionam as consultas online?',
      answer: 'As consultas são realizadas por videochamada em plataforma segura e confidencial. Você pode participar de qualquer lugar com conexão à internet, no conforto da sua casa.'
    },
    {
      question: 'Qual a diferença entre psicologia clínica e psicologia do futebol?',
      answer: 'A psicologia clínica foca no bem-estar emocional e mental geral, tratando questões como ansiedade, depressão e relacionamentos. A psicologia do futebol é especializada em atletas, trabalhando performance, controle emocional e preparação mental para competições.'
    },
    {
      question: 'Quanto tempo dura cada sessão?',
      answer: 'As sessões de psicologia clínica duram 50 minutos, enquanto as sessões de psicologia do futebol têm duração de 60 minutos.'
    },
    {
      question: 'Com que frequência devo fazer as sessões?',
      answer: 'A frequência ideal varia de acordo com suas necessidades. Geralmente recomenda-se sessões semanais, mas isso será definido em conjunto durante a avaliação inicial.'
    },
    {
      question: 'O atendimento online é seguro e confidencial?',
      answer: 'Sim, totalmente. Utilizamos plataformas seguras e seguimos rigorosamente o Código de Ética do Conselho Federal de Psicologia, garantindo total sigilo profissional.'
    },
    {
      question: 'Você atende atletas de que níveis?',
      answer: 'Atendo atletas de todos os níveis, desde iniciantes até profissionais, incluindo jogadores de base, amadores e profissionais de clubes.'
    },
    {
      question: 'Como é feito o primeiro atendimento?',
      answer: 'No primeiro atendimento fazemos uma avaliação inicial para entender suas necessidades, objetivos e histórico. A partir daí, traçamos um plano terapêutico personalizado.'
    },
    {
      question: 'Você oferece atendimento de emergência?',
      answer: 'Para situações de emergência, recomendo buscar serviços especializados como CVV (188) ou UPA. Ofereço atendimento agendado com horários flexíveis para melhor atendê-lo.'
    }
  ];

  // Combined Schema
  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ProfessionalService',
        name: 'Luan Lara Psicólogo',
        description: 'Psicólogo especializado em psicologia clínica e psicologia do futebol. Atendimento online com pós-graduação em psicologia clínica, esportiva e de grupos.',
        url: import.meta.env.VITE_SITE_URL || 'https://example.com',
        telephone: '+5519982330400',
        priceRange: '$$',
        areaServed: 'BR',
        availableLanguage: 'pt-BR',
        serviceType: ['Psicologia Clínica', 'Psicologia do Futebol', 'Psicologia Esportiva'],
        provider: {
          '@type': 'Person',
          name: 'Luan Lara',
          jobTitle: 'Psicólogo',
          description: 'Psicólogo CRP 06/223216 especializado em atendimentos clínicos individuais e psicologia do futebol'
        }
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqData.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      },
      {
        '@type': 'WebPage',
        name: 'Luan Lara, Psicólogo Clinico e do Esporte. Especializado em Futebol',
        description: 'Psicólogo especializado em psicologia clínica e psicologia do futebol. Atendimento online com pós-graduação em psicologia clínica, esportiva e de grupos. Agende sua consulta de psicologia clínica ou sessão de psicologia do futebol para melhorar sua performance mental e emocional.',
        url: import.meta.env.VITE_SITE_URL || 'https://example.com',
        inLanguage: 'pt-BR',
        isPartOf: {
          '@type': 'WebSite',
          name: 'Luan Lara Psicólogo',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com'
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Luan Lara, Psicólogo Clinico e do Esporte. Especializado em Futebol"
        description="Psicólogo especializado em psicologia clínica e psicologia do futebol. Atendimento online com pós-graduação em psicologia clínica, esportiva e de grupos. Agende sua consulta de psicologia clínica ou sessão de psicologia do futebol para melhorar sua performance mental e emocional."
        keywords="psicologia clínica, psicologia do futebol, psicólogo online"
        canonical="/"
        schema={combinedSchema}
      />
      {/* Header */}
      <header className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/9df808c5a5eda92f2f3db0cc4cefbc45/a965b4996718a266233e16beeb49802c.png"
                  alt="Luan Lara Psicólogo Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Luan Lara PSICÓLOGO</h1>
                <p className="text-sm text-gray-600">Psicologia Clínica & Esportiva</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('inicio')} className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Transforme sua mente e seu desempenho</button>
              <button onClick={() => scrollToSection('sobre')} className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Sobre</button>
              <button onClick={() => scrollToSection('servicos')} className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Serviços</button>
              <button onClick={() => window.REACT_APP_NAVIGATE('/instagram')} className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Instagram</button>
              <button onClick={() => scrollToSection('contato')} className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">Agendar Consulta</button>
            </nav>
            <button className="md:hidden p-2 text-gray-700 cursor-pointer">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section id="inicio" className="relative min-h-screen flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: 'url(https://static.readdy.ai/image/9df808c5a5eda92f2f3db0cc4cefbc45/fa9c85b68d949deadb9f2e7c94b480b0.png)' }}>
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative max-w-6xl mx-auto px-6 py-20 mt-16 w-full">
            <div className="w-full text-center text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Transforme sua <span className="text-teal-400">Mente</span> e<br />
                seu <span className="text-teal-400">Desempenho</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
                Psicólogo especializado em atendimentos clínicos individuais e psicologia do futebol. Consultas 100% online para seu conforto e conveniência.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button onClick={() => scrollToSection('contato')} className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                  Agendar Primeira Consulta
                </button>
                <button onClick={() => scrollToSection('servicos')} className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
                  Conhecer Serviços
                </button>
              </div>
              <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-video-chat-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">100% Online</h3>
                  <p className="text-gray-200">Atendimento por videochamada no conforto da sua casa</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-graduation-cap-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Especializado</h3>
                  <p className="text-gray-200">Pós-graduado em múltiplas áreas da psicologia</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-time-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Flexível</h3>
                  <p className="text-gray-200">Horários adaptados à sua rotina e necessidades</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="sobre" className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Sobre o <span className="text-teal-600">Profissional</span>
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Sou psicólogo com formação especializada e ampla experiência em atendimentos clínicos individuais e psicologia do esporte, com foco específico no futebol. Minha abordagem combina técnicas terapêuticas tradicionais com metodologias modernas adaptadas ao ambiente digital.
                </p>
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Formação Acadêmica</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-teal-600"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Pós-graduação em Psicologia Clínica</h4>
                        <p className="text-gray-600">Especialização em terapias individuais e processos terapêuticos</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-teal-600"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Pós-graduação em Psicologia de Grupos</h4>
                        <p className="text-gray-600">Desenvolvimento de equipes e dinâmicas grupais</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-teal-600"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Pós-graduação em Psicologia de Desastres</h4>
                        <p className="text-gray-600">Especialização em traumas e situações de crise</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-teal-600"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Pós-graduação em Psicologia do Esporte</h4>
                        <p className="text-gray-600">Especialista em Psicologia do Futebol</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://static.readdy.ai/image/2f6d0705b05dff710173b58a1c1b83a2/dfd1cf9c5be2566b4ee8141ae427ed9f.jpeg"
                  alt="Dr. Psicólogo - Especialista em Psicologia Clínica e Esportiva"
                  className="w-full rounded-lg shadow-xl object-cover object-top h-[600px]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="servicos" className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Meus <span className="text-teal-600">Serviços</span>
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Psicologia Clínica, Psicologia do futebol.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              {/* Clinical Psychology - FIRST */}
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-8 rounded-xl">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mr-4 p-2">
                    <img
                      src="https://static.readdy.ai/image/2f6d0705b05dff710173b58a1c1b83a2/58ee959fa38e5524cf262aff52944373.jpeg"
                      alt="Psicologia Clínica"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Psicologia Clínica</h3>
                    <p className="text-teal-700">Atendimento Individual</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Sessões de terapia individual focadas no seu bem-estar emocional e mental. Trabalho com diversas abordagens terapêuticas para atender suas necessidades específicas.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-teal-600"></i>
                    <span className="text-gray-700">Ansiedade e Depressão</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-teal-600"></i>
                    <span className="text-gray-700">Gestão de Estresse</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-teal-600"></i>
                    <span className="text-gray-700">Autoestima e Autoconhecimento</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-teal-600"></i>
                    <span className="text-gray-700">Relacionamentos Interpessoais</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-teal-600"></i>
                    <span className="text-gray-700">Trauma e Situações de Crise</span>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Duração da Sessão:</span>
                    <span className="font-semibold text-gray-900">50 minutos</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600">Modalidade:</span>
                    <span className="font-semibold text-gray-900">Online</span>
                  </div>
                </div>
                <button onClick={() => scrollToSection('contato')} className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                  Agendar Consulta de Psicologia Clínica
                </button>
              </div>

              {/* Football Psychology - SECOND */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-xl">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mr-4 p-2">
                    <img
                      src="https://static.readdy.ai/image/9df808c5a5eda92f2f3db0cc4cefbc45/b3497d023e03fc6415fb75c7a2c15f3c.png"
                      alt="Psicologia do Futebol Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Futebol</h3>
                    <p className="text-green-700">Especialização Esportiva</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Atendimento especializado para jogadores de futebol, focado no desenvolvimento mental, controle emocional e otimização do desempenho esportivo.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-green-600"></i>
                    <span className="text-gray-700">Preparação Mental Pré-Jogo</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-green-600"></i>
                    <span className="text-gray-700">Controle de Ansiedade Competitiva</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-green-600"></i>
                    <span className="text-gray-700">Foco e Concentração</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-green-600"></i>
                    <span className="text-gray-700">Recuperação Pós-Lesão</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-fill text-green-600"></i>
                    <span className="text-gray-700">Gestão de Pressão e Expectativas</span>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Duração da Sessão:</span>
                    <span className="font-semibold text-gray-900">60 minutos</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600">Modalidade:</span>
                    <span className="font-semibold text-gray-900">Online</span>
                  </div>
                </div>
                <button onClick={() => scrollToSection('contato')} className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap">
                  Agendar Consulta Esportiva
                </button>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-gray-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Como Funcionam as Consultas Online</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-calendar-check-line text-white text-2xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">1. Agendamento</h4>
                  <p className="text-gray-700">Entre em contato para agendar sua consulta no horário que melhor se adequa à sua rotina.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-video-chat-line text-white text-2xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">2. Videochamada</h4>
                  <p className="text-gray-700">Realizamos a sessão por videochamada em plataforma segura e confidencial.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-heart-pulse-line text-white text-2xl"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">3. Acompanhamento</h4>
                  <p className="text-gray-700">Desenvolvimento contínuo do seu processo terapêutico com acompanhamento personalizado.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Perguntas <span className="text-teal-600">Frequentes</span>
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Esclareça suas principais dúvidas sobre os atendimentos psicológicos online e como posso ajudar em sua jornada de desenvolvimento pessoal ou esportivo.
              </p>
            </div>
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-teal-600 text-xl transform transition-transform ${openFaq === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-4">Não encontrou a resposta que procurava?</p>
              <button onClick={() => scrollToSection('contato')} className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                Entre em Contato
              </button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contato" className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Entre em <span className="text-teal-600">Contato</span>
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Agende sua consulta ou tire suas dúvidas. Estou aqui para ajudar você em sua jornada de desenvolvimento pessoal e bem-estar mental.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Informações de Contato</h3>
                <div className="space-y-6 mb-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-video-chat-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Atendimento Online</h4>
                      <p className="text-gray-700">Consultas por videochamada</p>
                      <p className="text-gray-600 text-sm">Plataforma segura e confidencial</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Horários Flexíveis</h4>
                      <p className="text-gray-700">Segunda a Sexta: Não atendo pela manhã.</p>
                      <p className="text-gray-700">Sábados: 8h às 14h</p>
                      <p className="text-gray-600 text-sm">Horários especiais sob consulta</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Sigilo Profissional</h4>
                      <p className="text-gray-700">Confidencialidade garantida</p>
                      <p className="text-gray-600 text-sm">Conforme Código de Ética do CFP</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Especialidades</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-circle-fill text-teal-600"></i>
                      <span className="text-gray-700 text-sm">Psicologia Clínica</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-circle-fill text-teal-600"></i>
                      <span className="text-gray-700 text-sm">Psicologia do Futebol</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-circle-fill text-teal-600"></i>
                      <span className="text-gray-700 text-sm">Ansiedade</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-circle-fill text-teal-600"></i>
                      <span className="text-gray-700 text-sm">Performance Esportiva</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-circle-fill text-teal-600"></i>
                      <span className="text-gray-700 text-sm">Autoestima</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-circle-fill text-teal-600"></i>
                      <span className="text-gray-700 text-sm">Gestão de Estresse</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Agende sua Consulta</h3>
                <form id="contato-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      id="nome"
                      name="nome"
                      type="text"
                      required
                      value={formData.nome}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="seu@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone/WhatsApp
                    </label>
                    <input
                      id="telefone"
                      name="telefone"
                      type="tel"
                      value={formData.telefone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div>
                    <label htmlFor="tipo_atendimento" className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Atendimento *
                    </label>
                    <div className="relative">
                      <select
                        id="tipo_atendimento"
                        name="tipo_atendimento"
                        required
                        value={formData.tipo_atendimento}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 appearance-none bg-white"
                      >
                        <option value="">Selecione o tipo de atendimento</option>
                        <option value="psicologia-clinica">Psicologia Clínica Individual</option>
                        <option value="psicologia-futebol">Psicologia do Futebol</option>
                        <option value="consultoria">Consultoria Especializada</option>
                        <option value="duvidas">Apenas Dúvidas</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="mensagem" className="block text-sm font-medium text-gray-700 mb-2">
                      Mensagem *
                    </label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      required
                      maxLength={500}
                      rows={4}
                      value={formData.mensagem}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="Conte um pouco sobre o que você gostaria de trabalhar ou suas dúvidas..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{charCount}/500 caracteres</p>
                  </div>
                  {submitMessage && (
                    <div className={`p-4 rounded-lg ${submitMessage.includes('sucesso') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {submitMessage}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 text-white py-3 px-6 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium whitespace-nowrap"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 flex-shrink-0">
                  <img
                    src="https://static.readdy.ai/image/9df808c5a5eda92f2f3db0cc4cefbc45/a965b4996718a266233e16beeb49802c.png"
                    alt="Luan Lara Psicólogo Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Luan Lara Psicólogo</h3>
                  <p className="text-gray-400 text-sm">Psicologia Clínica & Esportiva</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Psicólogo especializado em atendimentos clínicos individuais e psicologia do futebol. Consultas 100% online com foco no seu bem-estar e desenvolvimento pessoal ou esportivo.
              </p>
              <div className="flex space-x-4">
                <a 
                  href="https://wa.me/5519982330400?text=Olá%2C%20Luan%20eu%20vim%20do%20site%20luanlarapsicologo%20e%20quero%20saber%20mais%20sobre%20as%20sessões" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                </a>
                <a 
                  href="https://www.instagram.com/luanlara.psicologo/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a 
                  href="https://www.linkedin.com/in/luan-lara-3334aa36a/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-line text-lg"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Navegação</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('inicio')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">
                    Início
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('sobre')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">
                    Sobre
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('servicos')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">
                    Serviços
                  </button>
                </li>
                <li>
                  <button onClick={() => window.REACT_APP_NAVIGATE('/instagram')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">
                    Instagram
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contato')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer">
                    Contato
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Especialidades</h4>
              <ul className="space-y-2 text-gray-300">
                <li>Psicologia Clínica</li>
                <li>Psicologia do Futebol</li>
                <li>Ansiedade</li>
                <li>Autoestima</li>
                <li>Gestão de Stress</li>
                <li>Desempenho Esportivo</li>
                <li>TDAH</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">© 2025 Luan Lara Psicólogo. Todos os direitos reservados.</p>
              <div className="flex items-center space-x-4 mt-4 md:mt-0">
                <span className="text-gray-400 text-sm">CRP: 06/223216</span>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/instagram')}
                  className="text-gray-400 hover:text-teal-400 transition-colors text-sm cursor-pointer"
                >
                  Me siga lá no Instagram! 👍
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
